// Phase 31 (plan 02): single chat bubble (text + media + optional sender label).
//
// Prop model:
//   - `isMine: boolean` — caller decides who is "me". Right-aligned mint-green
//     bubble when true; left-aligned white bubble when false. Identical visuals
//     to the pre-extraction code; only the prop name changed (was `isFromUser`).
//   - `senderLabel?: string` — optional caller-provided label. Rendered ONLY
//     when `!isMine` (left side). Right-side bubbles NEVER show a sender label
//     (Telegram/iMessage convention).
//   - `userFirstName?: string` + `message.technicianName` — retained for the
//     technician miniapp shim, which does NOT pass `senderLabel`. The shim's
//     existing role-based fallback continues to drive the left-side label there.
//
// INVARIANT: do NOT branch on message.ticketId. The Bayon embed adapter
// (plan 04, bayonMessageToChatMessage) intentionally sets ticketId='' because
// Bayon messages have no ticket scope. Only MessageList's gated divider
// (showTicketDividers={true}) is allowed to read ticketId.

import type { Message } from '../../types';
import { MediaContent, MEDIA_PLACEHOLDERS, STANDALONE_MEDIA_TYPES, NON_MEDIA_LABEL_TOKENS, NonMediaLabel } from './MediaContent';

/**
 * Phase 33 — shared "Replying to X" sub-element rendered inside any
 * bubble whose `replyToTarget` is set. Wrapped in a button when an
 * `onClick` handler is provided (carrying the target's DB id, which
 * the parent uses to scroll the original message into view). Falls
 * back to a plain div when no handler is supplied so unit tests and
 * the standalone-media branch don't depend on a callback. Styling is
 * intentionally identical across both call sites — the only "tappable"
 * affordance is the cursor: pointer + active-press scale.
 */
function ReplyTargetPreview({
  target,
  fromSelf,
  onClick,
}: {
  target: NonNullable<Message['replyToTarget']>;
  /** true when the OUTER message is from the user; flips "Replying to X"
   *  label between "Support" and "User". */
  fromSelf: boolean;
  onClick?: (targetId: number) => void;
}) {
  const tappable = !!(onClick && target.id != null);
  const body = (
    <>
      <div className="text-[11px] font-semibold leading-tight" style={{ color: '#007AFF' }}>
        Replying to {fromSelf ? 'Support' : 'User'}
      </div>
      <div className="text-[12px] text-gray-600 leading-snug line-clamp-2 break-words">
        {previewTextForBubble(target)}
      </div>
    </>
  );
  const baseClass = 'mb-1.5 pl-2 border-l-[3px] flex flex-col text-left';
  const style = { borderColor: '#007AFF' };
  if (!tappable) {
    return (
      <div className={baseClass} style={style}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation(); // don't bubble up to the bubble's own click handlers (media open, etc.)
        onClick!(target.id!);
      }}
      className={`${baseClass} w-full rounded transition active:scale-[0.985]`}
      style={style}
      aria-label="Jump to original message"
    >
      {body}
    </button>
  );
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Phase 33 (plan 33-06) — produce the short preview string rendered
 * inside the bubble's in-bubble quoted-preview sub-element. Mirrors the
 * shape of the composer-side ReplyContextBar.previewText() for visual
 * consistency on both sides of the conversation.
 *
 * Truncation: 120 chars (vs ReplyContextBar's 80) because the in-bubble
 * preview has slightly more horizontal real-estate and line-clamp-2 will
 * wrap the second half. Whitespace is collapsed before slicing so a
 * multiline target excerpt doesn't render with awkward gaps.
 *
 * Media fallback: when there's no text, fall back to a generic
 * media-type label. The server JOIN intentionally doesn't carry the
 * target's file_name (the messages table has no file_name column today),
 * so document / audio targets show the generic label only.
 *
 * Defensive fallback: when neither text nor mediaType is set — the
 * target was deleted before the inbound landed, OR the JOIN edge-cased
 * — return "(Original message unavailable)" so the bubble degrades
 * gracefully instead of rendering an empty preview strip.
 */
function previewTextForBubble(target: NonNullable<Message['replyToTarget']>): string {
  if (target.text && target.text.trim()) {
    const collapsed = target.text.replace(/\s+/g, ' ').trim();
    return collapsed.length > 120 ? collapsed.slice(0, 120) + '…' : collapsed;
  }
  switch (target.mediaType) {
    case 'photo': return 'Photo';
    case 'document': return 'Document';
    case 'voice': return 'Voice message';
    case 'video': return 'Video';
    case 'audio': return 'Audio';
    default: return '(Original message unavailable)';
  }
}

export interface MessageBubbleProps {
  message: Message;
  /** Caller decides who is "me" — right side (mint-green) when true. */
  isMine: boolean;
  /** Optional explicit label rendered above LEFT-side bubbles only. */
  senderLabel?: string;
  /** Fallback used by the technician miniapp shim when senderLabel is not provided. */
  userFirstName?: string;
  /**
   * When true, render the sender label above RIGHT-side (mine) bubbles too.
   * Default false (Telegram/iMessage convention — your own messages are
   * unlabeled). Iframe-embedded mode flips this on so multi-author chats
   * label every bubble. The technician shim sets this only when
   * `?mode=embedded` is in the URL so the normal Telegram miniapp flow keeps
   * today's appearance (no label above the technician's own outgoing bubbles).
   */
  showLabelOnMine?: boolean;
  /** Phase 33 — tap on the in-bubble quoted preview to jump to the
   *  original message. Receives the target's DB id; caller scrolls
   *  the corresponding bubble into view and flashes a highlight.
   *  Absent → preview renders as plain text (no tap affordance). */
  onReplyTargetClick?: (targetMessageId: number) => void;
}

export function MessageBubble({ message, isMine, senderLabel, userFirstName, showLabelOnMine = false, onReplyTargetClick }: MessageBubbleProps) {
  const timestamp = formatTime(message.timestamp);

  // v4.1: every delete is "Delete for everyone" — the server keeps these
  // rows for audit but tags deletedForEveryone:true; render a slim
  // placeholder so the conversation flow stays readable. Legacy "Delete
  // for me" rows from pre-strict-mode are filtered out server-side and
  // never arrive here.
  if (message.deletedForEveryone) {
    const isIncoming = !isMine;
    const placeholderBg = isIncoming ? '#F2F4F6' : '#E1ECDB';
    const placeholderTextColor = isIncoming ? '#7E919A' : '#5E7C53';
    return (
      <div className={`flex mb-1 ${isIncoming ? 'justify-start pl-3' : 'justify-end pr-3'}`}>
        <div
          className="max-w-[80%] px-3 py-2 rounded-2xl italic text-sm"
          style={{ background: placeholderBg, color: placeholderTextColor }}
        >
          Message deleted
          <span className="ml-2 text-[11px] opacity-70 not-italic tabular-nums">{timestamp}</span>
        </div>
      </div>
    );
  }

  const hasMedia = !!(message.hasMedia && message.mediaUrl);
  // Non-downloadable types (location/poll/dice/unsupported) are stored as a
  // label token with no real media — render the card, never the raw "[token]".
  const isLabel = !hasMedia && !!message.text && NON_MEDIA_LABEL_TOKENS.has(message.text);
  const hasText = !!(
    message.text
    && !MEDIA_PLACEHOLDERS.has(message.text)
    && !isLabel
  );
  const isStandaloneMediaMessage = !!(
    message.mediaType
    && hasMedia
    && !hasText
    && STANDALONE_MEDIA_TYPES.has(message.mediaType)
  );

  const isTelegramSticker = message.text === '[sticker]';

  // Left-side bubble = incoming (white). Right-side bubble = mine (mint-green).
  // In the technician miniapp the technician (chatroom-detail viewer) is the
  // one whose messages go RIGHT, so the shim passes isMine={msg.from !== 'user'}
  // and isFromUser-style left/right behavior is preserved end-to-end.
  const isIncoming = !isMine;

  // Telegram colour scheme — visuals identical to pre-extraction.
  const bubbleBg = isIncoming ? '#FFFFFF' : '#E7FECC';
  const tailFill = isIncoming ? '#FFFFFF' : '#E7FECC';
  const bubbleRadius = isIncoming ? '0px 18px 18px 18px' : '18px 0px 18px 18px';
  const timeColor = isIncoming ? '#7E919A' : '#439636';
  const senderColor = isIncoming ? '#007AFF' : '#439636';

  // Sender label rendering:
  //   - Left-side (incoming): always rendered (today's behavior).
  //   - Right-side (mine):   rendered ONLY when showLabelOnMine is true. The
  //     technician shim turns this on for iframe-embedded mode so multi-author
  //     chats label every bubble; the normal Telegram miniapp keeps the
  //     today-behavior of unlabeled outgoing bubbles.
  // Source preference for the label:
  //   1. Caller-provided senderLabel (Bayon path — passes msg.sender.displayName).
  //   2. Fallback for the technician miniapp shim, branched by role:
  //      - End-user message → userFirstName
  //      - Technician message (yours or another technician's) → message.technicianName ?? 'Support'
  let resolvedSenderLabel: string | null = null;
  if (isIncoming || showLabelOnMine) {
    if (senderLabel != null) {
      resolvedSenderLabel = senderLabel;
    } else {
      resolvedSenderLabel = message.from === 'user'
        ? (userFirstName || '')
        : (message.technicianName ?? 'Support');
    }
  }

  if (isStandaloneMediaMessage) {
    return (
      <div
        className={`flex mb-1 ${isIncoming ? 'justify-start pl-3' : 'justify-end pr-3'}`}
      >
        <div className="max-w-[80%]" data-message-bubble>
          {resolvedSenderLabel != null && (
            <div
              className="px-1 mb-1 text-[11px] font-semibold leading-tight"
              style={{ color: senderColor }}
            >
              {resolvedSenderLabel}
            </div>
          )}

          {/* Phase 33 (plan 33-06) — in-bubble quoted preview for
              standalone-media messages (e.g. user replies to bot text
              with a photo, no caption). The normal bubble body branch
              renders its own copy of this block below; this duplicate
              is intentional because the standalone-media branch returns
              early without ever reaching the normal bubble body. iOS
              blue accent stripe matches ReplyContextBar (plan 33-05)
              for visual continuity. */}
          {message.replyToTarget && (
            <ReplyTargetPreview
              target={message.replyToTarget}
              fromSelf={message.from === 'user'}
              onClick={onReplyTargetClick}
            />
          )}

          <MediaContent
            mediaType={message.mediaType}
            mediaKey={message.mediaKey}
            fallbackUrl={message.mediaUrl ?? ''}
            fileName={message.fileName}
            fileSize={message.fileSize}
            isFromUser={isIncoming}
            presentation="bare"
            isTelegramSticker={isTelegramSticker}
            durationSeconds={message.durationSeconds}
            seq={message.id}
          />

          <div
            className="px-1 text-[11px] text-right mt-1 leading-tight"
            style={{ color: timeColor }}
          >
            {timestamp}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex mb-1 ${isIncoming ? 'justify-start pl-3' : 'justify-end pr-3'}`}
    >
      <div className="max-w-[80%]">
        {resolvedSenderLabel != null && (
          <div
            className="px-1 mb-1 text-[11px] font-semibold leading-tight"
            style={{ color: senderColor }}
          >
            {resolvedSenderLabel}
          </div>
        )}

        {/*
          drop-shadow on this container applies to the entire visual shape
          (tail SVG + bubble body) as one unified shadow.
        */}
        <div
          className="relative"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.13))' }}
        >
          {/* ── Left tail (received) ── */}
          {isIncoming && (
            <svg
              width="8"
              height="13"
              viewBox="0 0 8 13"
              fill="none"
              aria-hidden
              style={{ position: 'absolute', top: 0, left: -8 }}
            >
              {/* triangle: top-right → bottom-right → bottom-left */}
              <path d="M8 0 L8 13 L0 13 Z" fill={tailFill} />
            </svg>
          )}

          {/* ── Bubble body ── */}
          <div
            className="px-3 pt-2 pb-1.5"
            style={{ background: bubbleBg, borderRadius: bubbleRadius }}
          >

            {/* Phase 33 (plan 33-06) — in-bubble quoted preview. Renders
                when the user quoted one of our bot messages in Telegram
                and we resolved the target row server-side (LEFT JOIN in
                MessageService.getMessagesFor*). Visually consistent with
                composer-side ReplyContextBar (plan 33-05): iOS blue
                accent stripe + two-line preview. Sits inside the bubble
                body so it inherits the bubble bg + radius — no extra
                elevation/shadow. Renders FIRST so it sits above the
                media content and text body (Telegram convention). */}
            {message.replyToTarget && (
              <ReplyTargetPreview
                target={message.replyToTarget}
                fromSelf={message.from === 'user'}
                onClick={onReplyTargetClick}
              />
            )}

            {/* Media content */}
            {hasMedia && (
              <MediaContent
                mediaType={message.mediaType}
                mediaKey={message.mediaKey}
                fallbackUrl={message.mediaUrl ?? ''}
                fileName={message.fileName}
                fileSize={message.fileSize}
                isFromUser={isIncoming}
                isTelegramSticker={isTelegramSticker}
                durationSeconds={message.durationSeconds}
                seq={message.id}
              />
            )}

            {/* Non-media label card (location / poll / dice / unsupported) */}
            {isLabel && <NonMediaLabel text={message.text!} />}

            {/* Text */}
            {hasText && (
              <p className="text-[15px] text-[#22282B] leading-snug whitespace-pre-wrap break-words">
                {message.text}
              </p>
            )}
          </div>

          {/* ── Right tail (sent) ── */}
          {!isIncoming && (
            <svg
              width="8"
              height="13"
              viewBox="0 0 8 13"
              fill="none"
              aria-hidden
              style={{ position: 'absolute', top: 0, right: -8 }}
            >
              {/* mirror of left tail */}
              <path d="M0 0 L0 13 L8 13 Z" fill={tailFill} />
            </svg>
          )}
        </div>

        <div
          className="px-1 text-[11px] text-right mt-1 leading-tight"
          style={{ color: timeColor }}
        >
          {timestamp}
        </div>
      </div>
    </div>
  );
}
